import {
  type ExportRequest,
  type ExportSummary,
  cancelExport,
  getExportStatus,
  requestExport,
} from '@/lib/api/reports-client.js';
import { useCallback, useEffect, useRef, useState } from 'react';

export type ExportState =
  | { phase: 'idle' }
  | { phase: 'starting' }
  | { phase: 'polling'; exportId: string; progress: number }
  | { phase: 'completed'; exportId: string; downloadUrl: string }
  | { phase: 'failed'; message: string }
  | { phase: 'cancelled' };

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_DURATION_MS = 10 * 60 * 1000; // 10 minutes

export function useExport(tenantReportId: string) {
  const [state, setState] = useState<ExportState>({ phase: 'idle' });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const exportIdRef = useRef<string | null>(null);
  const pollStartedAtRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    pollStartedAtRef.current = null;
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const startExport = useCallback(
    async (body: ExportRequest, idempotencyKey?: string) => {
      setState({ phase: 'starting' });
      try {
        const summary = await requestExport(tenantReportId, body, idempotencyKey);
        exportIdRef.current = summary.exportId;

        if (summary.status === 'completed' && summary.downloadUrl !== undefined) {
          setState({
            phase: 'completed',
            exportId: summary.exportId,
            downloadUrl: summary.downloadUrl,
          });
          return;
        }

        setState({ phase: 'polling', exportId: summary.exportId, progress: 0 });
        pollStartedAtRef.current = Date.now();

        intervalRef.current = setInterval(async () => {
          if (
            pollStartedAtRef.current !== null &&
            Date.now() - pollStartedAtRef.current > MAX_POLL_DURATION_MS
          ) {
            stopPolling();
            setState({
              phase: 'failed',
              message: 'Exportación tomó demasiado tiempo. Reintenta o contacta soporte.',
            });
            return;
          }
          try {
            const status = await getExportStatus(summary.exportId);
            handleStatusUpdate(status);
          } catch {
            stopPolling();
            setState({
              phase: 'failed',
              message: 'Error al verificar el estado de la exportación.',
            });
          }
        }, POLL_INTERVAL_MS);
      } catch (err) {
        setState({
          phase: 'failed',
          message: err instanceof Error ? err.message : 'Error al iniciar la exportación.',
        });
      }
    },
    [tenantReportId, stopPolling],
  );

  function handleStatusUpdate(summary: ExportSummary) {
    if (summary.status === 'completed') {
      stopPolling();
      const downloadUrl = summary.downloadUrl ?? '';
      setState({ phase: 'completed', exportId: summary.exportId, downloadUrl });
    } else if (summary.status === 'failed') {
      stopPolling();
      setState({ phase: 'failed', message: 'La exportación falló en el servidor.' });
    } else if (summary.status === 'cancelled') {
      stopPolling();
      setState({ phase: 'cancelled' });
    } else {
      setState((prev) => ({
        phase: 'polling',
        exportId: summary.exportId,
        progress: summary.progress ?? (prev.phase === 'polling' ? prev.progress : 0),
      }));
    }
  }

  const cancel = useCallback(async () => {
    const id = exportIdRef.current;
    if (id === null) return;
    stopPolling();
    try {
      await cancelExport(id);
    } finally {
      setState({ phase: 'cancelled' });
    }
  }, [stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    exportIdRef.current = null;
    pollStartedAtRef.current = null;
    setState({ phase: 'idle' });
  }, [stopPolling]);

  return { state, startExport, cancel, reset };
}
